import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  OrganizationDetail,
  Organization as OrganizationType,
} from '@rtkwlf/eloc';
import * as Sentry from '@sentry/react';
import isEqual from 'lodash/isEqual';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { useOrganizationDetail } from '@apis/eloc/queries/use-organization-detail';
import { useOrganization } from '@apis/rendall/queries/use-organization';
import { OrganizationContext } from '@packages/legacy/organization-context';
import { STORAGE_KEYS } from 'ae-constants/constants';
import { useSelectedOrganizationContext } from 'ae-kernel/selected-organization-provider/selected-organization-provider';

export const Organizations = ({
  children,
  waitForRendallUrl,
  loadingElement,
  errorElement,
}: React.PropsWithChildren<{
  waitForRendallUrl: boolean;
  loadingElement: React.ReactElement;
  errorElement: React.ReactElement;
}>) => {
  const navigate = useNavigate();

  const [query, setQuery] = useQueryParams({
    id: StringParam,
    org: StringParam,
  });

  const urlOrg = useMemo(() => query.org || query.id, [query.id, query.org]);

  // If user enters org query-param in the url, then the default should be that query-param.
  // Or else, the default should be the local storage value.
  // or else, the default value should be empty.
  const defaultSelected = useMemo(() => {
    if (urlOrg) {
      return {
        customerID: '',
        id: urlOrg,
        name: '',
        rendallURL: '',
      };
    }

    const selectedOrgFromStorage = window.localStorage.getItem(
      STORAGE_KEYS.AWN_ORGANIZATION,
    );

    if (selectedOrgFromStorage) {
      return JSON.parse(selectedOrgFromStorage);
    }
    return { customerID: '', id: '', name: '', rendallURL: '' };
  }, [urlOrg]);

  const [querySelectedOrg] = useState<OrganizationType>();

  const [org, setOrg] = useState<OrganizationType>(defaultSelected);

  const [callbackFn, setCallbackFn] = useState<() => void>();

  const selectOrganization = useCallback(
    (input: OrganizationType, callBack?: () => void) => {
      setOrg(input);
      if (callBack) {
        setCallbackFn(callBack);
      }
    },
    [setOrg],
  );

  const isFirstRender = useRef(true);
  // This block of code is to run the callbackFn on organization switch.
  // Though it needs to run only after the organization state has been updated.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // toggle flag after first render/mounting
      return;
    }
    if (callbackFn) {
      callbackFn();
    }
  }, [callbackFn, org]);

  const organizationDetailQueryId = urlOrg || org.id;
  const {
    isError: isErrorOrganization,
    data: organization,
    isFetching: isLoadingOrganization,
    error: errorOrganization,
  } = useOrganizationDetail(organizationDetailQueryId, {
    enabled: Boolean(organizationDetailQueryId),
  });

  useEffect(() => {
    if (organization) {
      window.localStorage.setItem(
        STORAGE_KEYS.AWN_ORGANIZATION,
        JSON.stringify(organization),
      );
    } else {
      window.localStorage.removeItem(STORAGE_KEYS.AWN_ORGANIZATION);
    }
  }, [organization]);

  const selectedOrganization = useMemo<OrganizationDetail>(() => {
    const newSelectedOrganization = organization || defaultSelected;
    return newSelectedOrganization;
  }, [organization, defaultSelected]);

  const {
    selectedOrganization: selectedOrganizationFromContext,
    setSelectedOrganization,
  } = useSelectedOrganizationContext();

  const { isError } = useOrganization(
    selectedOrganization.id,
    selectedOrganization.rendallURL,
  );

  if (isError) {
    navigate('/unauthorized');
  }

  useEffect(() => {
    if (
      isLoadingOrganization ||
      isErrorOrganization ||
      isEqual(selectedOrganizationFromContext, selectedOrganization)
    ) {
      return;
    }
    setSelectedOrganization(selectedOrganization);
  }, [
    isErrorOrganization,
    isLoadingOrganization,
    selectedOrganization,
    selectedOrganizationFromContext,
    setSelectedOrganization,
  ]);

  useEffect(() => {
    if (isErrorOrganization) {
      setSelectedOrganization(undefined);
    }
  }, [isErrorOrganization, setSelectedOrganization]);

  const orgValue = useMemo(
    () => ({
      selectOrganization,
      selectedOrganization,
    }),
    [selectOrganization, selectedOrganization],
  );

  const showError = useMemo(
    () => isErrorOrganization && !!(urlOrg && !querySelectedOrg),
    [isErrorOrganization, urlOrg, querySelectedOrg],
  );

  useEffect(() => {
    if (organization) {
      setQuery({ id: undefined, org: undefined });
    }
  }, [setQuery, organization]);

  if (showError) {
    Sentry.captureException(errorOrganization, {
      tags: { awth: 'orgCtxError' },
    });
    return errorElement;
  }

  const isReadyToRenderChildren =
    !waitForRendallUrl || selectedOrganization.rendallURL;

  return (
    <OrganizationContext.Provider
      value={orgValue}
      key={selectedOrganization.id}
    >
      {isReadyToRenderChildren ? children : loadingElement}
    </OrganizationContext.Provider>
  );
};
